import React, { useState, useEffect } from "react";
import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import { Loader, ThemeProvider, Alert } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";

// ---------------------------------------------------------------------------
// BUILD STAMP — bump this string whenever you change this app so you can tell a
// fresh build from a stale one. It is logged to the console AND shown as a tiny
// on-screen badge (handy on mobile where the console is hard to reach). If you
// don't see this exact value on screen / in the console, you're on an OLD build
// — rebuild/redeploy (or hard-refresh the dev server).
// ---------------------------------------------------------------------------
const BUILD_TAG = "vkyc-liveliness · preview-tee v5 (smooth) · 2026-07-19";
try {
  // eslint-disable-next-line no-console
  console.log("%c[spense-liveliness] BUILD:", "color:#7c3aed;font-weight:bold", BUILD_TAG);
} catch {}

// Are we running inside an iframe (the in-call agent flow), or as a top-level
// page (the legacy customer self-service flow)? When embedded we do NOT
// navigate on completion — instead we notify the parent (CustomerCall) via
// postMessage so it can close the overlay, restore the call camera, and relay
// the status to the agent over PeerJS. When top-level we keep the original
// redirect behaviour.
const isEmbedded = (() => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — that itself means we are
    // embedded in a different-origin parent.
    return true;
  }
})();

// Post a lifecycle event to the parent window. targetOrigin is "*" because the
// parent (videntia) origin differs from this app's origin and is not known at
// build time; the payload carries no sensitive data (status signals only) and
// the parent validates event.origin against this app's known origin.
function notifyParent(event, detail) {
  if (!isEmbedded) return;
  try {
    window.parent.postMessage(
      { source: "spense-liveliness", event, detail: detail ?? null },
      "*"
    );
  } catch (err) {
    console.error("Failed to postMessage to parent:", err);
  }
}

// ---------------------------------------------------------------------------
// Live camera preview tee (embedded / in-call flow only)
//
// AWS's FaceLivenessDetector owns the camera exclusively and does not expose
// its MediaStream, so the agent cannot see the customer during the check. On
// mobile we also cannot open the camera a second time. To give the agent a
// live view for guiding face positioning, we wrap getUserMedia, CLONE the
// video track AWS acquires (clone() shares the same camera source — it does
// NOT re-open the device), draw the clone to a canvas a few times per second,
// and relay JPEG frames to the parent (CustomerCall), which forwards them to
// the agent over the PeerJS data channel.
// ---------------------------------------------------------------------------
const PREVIEW_FPS = 8; // frames per second relayed to the agent (was 2 — choppy)
const PREVIEW_WIDTH = 320; // downscaled width (keeps data-channel frames small)
const PREVIEW_JPEG_QUALITY = 0.5;

let previewCleanup = null;
let previewFrameCount = 0;

function stopPreviewTee() {
  if (previewCleanup) {
    try {
      previewCleanup();
    } catch {}
    previewCleanup = null;
  }
}

function startPreviewTee(stream) {
  if (!isEmbedded) return;
  stopPreviewTee();

  const videoTrack = stream.getVideoTracks?.()[0];
  if (!videoTrack) return;

  let clonedTrack;
  try {
    clonedTrack = videoTrack.clone();
  } catch (err) {
    console.error("[preview] Failed to clone video track:", err);
    return;
  }

  const previewStream = new MediaStream([clonedTrack]);
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("muted", "");
  video.setAttribute("autoplay", "");
  // IMPORTANT: attach to the DOM (visually hidden). Many mobile browsers will
  // not decode frames for a DETACHED <video>, which would make drawImage
  // produce nothing — the reason a preview can silently fail on phones.
  video.style.cssText =
    "position:fixed;left:-10px;top:-10px;width:2px;height:2px;opacity:0;pointer-events:none;";
  document.body.appendChild(video);
  video.srcObject = previewStream;
  const playPromise = video.play();
  if (playPromise && typeof playPromise.catch === "function") {
    playPromise.catch((e) =>
      console.warn("[preview] play() blocked:", e?.message || e)
    );
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  previewFrameCount = 0;

  const interval = setInterval(() => {
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || video.readyState < 2 || clonedTrack.readyState !== "live")
      return;

    const scale = PREVIEW_WIDTH / vw;
    canvas.width = PREVIEW_WIDTH;
    canvas.height = Math.max(1, Math.round(vh * scale));

    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/jpeg", PREVIEW_JPEG_QUALITY);
      notifyParent("previewFrame", dataUrl);
      if (previewFrameCount === 0) {
        console.log(
          "[preview] first frame sent",
          `${canvas.width}x${canvas.height}`,
          `${dataUrl.length} bytes`
        );
      }
      previewFrameCount++;
    } catch (err) {
      console.error("[preview] frame capture failed:", err);
    }
  }, Math.round(1000 / PREVIEW_FPS));

  previewCleanup = () => {
    clearInterval(interval);
    try {
      clonedTrack.stop();
    } catch {}
    try {
      video.pause();
    } catch {}
    try {
      video.srcObject = null;
    } catch {}
    try {
      video.remove();
    } catch {}
    console.log("[preview] tee stopped after", previewFrameCount, "frames");
  };

  // If AWS stops its track (check finished), tear the tee down too.
  try {
    videoTrack.addEventListener("ended", stopPreviewTee, { once: true });
  } catch {}
}

// Wrap getUserMedia ONCE so we can observe the stream AWS acquires. Installed
// at module load so it is in place before the detector mounts. No-op unless
// embedded and the acquired stream actually has a video track.
if (
  typeof navigator !== "undefined" &&
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function" &&
  !navigator.mediaDevices.__spensePreviewPatched
) {
  const originalGUM = navigator.mediaDevices.getUserMedia.bind(
    navigator.mediaDevices
  );
  navigator.mediaDevices.getUserMedia = async (constraints) => {
    const acquired = await originalGUM(constraints);
    try {
      if (isEmbedded && acquired.getVideoTracks?.().length > 0) {
        console.log("[preview] getUserMedia intercepted — starting tee");
        startPreviewTee(acquired);
      }
    } catch (err) {
      console.error("[preview] tee setup failed:", err);
    }
    return acquired;
  };
  navigator.mediaDevices.__spensePreviewPatched = true;
}

function App() {
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);

  useEffect(() => {
    const decodeToken = async () => {
      setLoading(true);
      const redirect_url = new URLSearchParams(window.location.search).get(
        "redirect_url"
      );
      const session_id = new URLSearchParams(window.location.search).get(
        "session_id"
      );

      if (session_id) {
        try {
          setSessionId(session_id);
          setRedirectUrl(redirect_url);
        } catch (err) {
          console.log("Invalid token", err);
        } finally {
          setLoading(false);
        }
      } else {
        console.log("No token found");
      }
    };

    decodeToken();
  }, []);

  // Once we have a session id and the detector is about to render (start screen
  // is disabled, so the check begins immediately), tell the parent the check
  // has started. The agent's awareness panel flips to "customer positioning
  // their face…" on this signal.
  useEffect(() => {
    if (!loading && sessionId) {
      notifyParent("started");
    }
  }, [loading, sessionId]);

  // Ensure the preview tee is torn down if the component unmounts for any
  // reason (parent closes the overlay, navigation, etc.).
  useEffect(() => {
    return () => stopPreviewTee();
  }, []);

  const handleComplete = () => {
    stopPreviewTee();
    notifyParent("analysisComplete");
    // Top-level (self-service) flow keeps navigating back; embedded flow lets
    // the parent close the overlay.
    if (!isEmbedded) {
      window.location.href = redirectUrl;
    }
  };

  const handleError = (error) => {
    stopPreviewTee();
    console.error(error);
    notifyParent("error", error?.state || error?.message || "LIVENESS_ERROR");
    if (!isEmbedded) {
      window.location.href = redirectUrl;
    }
  };

  const detector = loading ? null : (
    <FaceLivenessDetector
      className="amplify-liveness-detector"
      sessionId={sessionId}
      region="ap-south-1"
      // Skip AWS's start screen (the "Start video check" button) so the check
      // begins as soon as it mounts — the agent is the only one who presses
      // "Start". IMPORTANT: in @aws-amplify/ui-react-liveness v3.1.4 the prop
      // that actually skips the start screen is `disableStartScreen` (verified
      // against the machine's `shouldSkipStartScreen` guard). `disableInstruction
      // Screen` does NOT exist in this version and is ignored — it is kept only
      // for forward-compat with newer majors that renamed the prop.
      disableStartScreen={true}
      disableInstructionScreen={true}
      onAnalysisComplete={handleComplete}
      onError={handleError}
      components={{
        PhotosensitiveWarning: () => (
          <Alert
            variation="warning"
            isDismissible={false}
            hasIcon={true}
            heading=""
          >
            This check flashes different colors. Use caution if you are
            photosensitive.
          </Alert>
        ),
      }}
    />
  );

  // Embedded (in-call) flow: render ONLY the detector, full-bleed and
  // chrome-free, so it blends into the customer's video-call journey instead of
  // looking like a separate "spense liveliness" page. No header, logo, back
  // button, or card — before, during, or after the check.
  if (isEmbedded) {
    return (
      <ThemeProvider>
        <div
          style={{
            position: "fixed",
            inset: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#000",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Loader size="large" variation="linear" />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                maxWidth: 480,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {detector}
            </div>
          )}
        </div>
      </ThemeProvider>
    );
  }

  // Top-level (self-service) flow keeps the original branded page.
  return (
    <ThemeProvider>
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-3 rounded-lg shadow-lg">
          <div className="flex items-center w-full relative mb-4">
            <span
              className="material-symbols-outlined cursor-pointer text-2xl absolute left-0"
              onClick={() => {
                window.location.href = redirectUrl;
              }}
            >
              arrow_back
            </span>
            <h1 className="text-2xl font-semibold text-gray-800 leading-tight mx-auto">
              Active Liveliness Check
            </h1>
          </div>

          {loading ? <Loader /> : detector}

          <div className="mt-8 flex items-center justify-center">
            <div className="flex items-center">
              <img src="/spense_logo.png" alt="Spense Logo" className="w-[22.72px] h-[20.91px]" />
              <p className="w-[43.83px] h-[11.03px] text-[#240663]">spense</p>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default App;
